/**
 * Bank Details Form Component
 * Allows drivers to add/update their bank account for payouts
 */
import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Building2, 
  CreditCard, 
  User, 
  Save, 
  Loader2,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

// Jamaica banks list with short codes for mapping
const JAMAICA_BANKS = [
  { value: 'NCB', label: 'National Commercial Bank (NCB)' },
  { value: 'BNS', label: 'Bank of Nova Scotia Jamaica' },
  { value: 'CIBC', label: 'CIBC FirstCaribbean' },
  { value: 'Sagicor', label: 'Sagicor Bank Jamaica' },
  { value: 'JMMB', label: 'JMMB Bank' },
  { value: 'JN', label: 'JN Bank' },
  { value: 'FGB', label: 'First Global Bank' },
  { value: 'Citibank', label: 'Citibank Jamaica' },
  { value: 'Other', label: 'Other' }
];

export default function BankDetailsForm({ onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasDetails, setHasDetails] = useState(false);
  const [formData, setFormData] = useState({
    bank_name: '',
    branch_name: '',
    account_number: '',
    account_type: 'savings',
    account_holder_name: ''
  });

  useEffect(() => {
    fetchBankDetails();
  }, []);

  const fetchBankDetails = async () => {
    try {
      const response = await api.get('/drivers/bank-details');
      if (response.data.bank_details) {
        const details = response.data.bank_details;
        setFormData({
          bank_name: details.bank_name || '',
          branch_name: details.branch_name || '',
          account_number: '', // Don't prefill for security
          account_type: details.account_type || 'savings',
          account_holder_name: details.account_holder_name || ''
        });
        setHasDetails(true);
      }
    } catch (error) {
      console.error('Error fetching bank details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!formData.bank_name) {
      toast.error('Please select your bank');
      return;
    }
    // Only require account number if no existing details
    if (!hasDetails && (!formData.account_number || formData.account_number.length < 6)) {
      toast.error('Please enter a valid account number');
      return;
    }
    if (!formData.account_holder_name) {
      toast.error('Please enter the account holder name');
      return;
    }

    setSaving(true);
    try {
      // Only send account_number if it was entered
      const dataToSend = { ...formData };
      if (!dataToSend.account_number) {
        delete dataToSend.account_number;
      }
      await api.put('/drivers/bank-details', dataToSend);
      toast.success('Bank details saved successfully!');
      setHasDetails(true);
      if (onSaved) onSaved();
    } catch (error) {
      console.error('Error saving bank details:', error);
      toast.error(error.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-slate-900 border-slate-800">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-slate-900 border-slate-800">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white">
          <Building2 className="h-5 w-5 text-emerald-500" />
          Bank Details for Payouts
          {hasDetails && (
            <CheckCircle className="h-4 w-4 text-emerald-500 ml-2" />
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Bank Name */}
          <div>
            <Label className="text-slate-300">Bank Name *</Label>
            <select
              value={formData.bank_name}
              onChange={(e) => setFormData({...formData, bank_name: e.target.value})}
              className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="">Select your bank</option>
              {JAMAICA_BANKS.map(bank => (
                <option key={bank.value} value={bank.value}>{bank.label}</option>
              ))}
            </select>
          </div>

          {/* Branch Name */}
          <div>
            <Label className="text-slate-300">Branch Name</Label>
            <Input
              type="text"
              value={formData.branch_name}
              onChange={(e) => setFormData({...formData, branch_name: e.target.value})}
              placeholder="e.g., Half Way Tree Branch"
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
            />
          </div>

          {/* Account Number */}
          <div>
            <Label className="text-slate-300">Account Number {!hasDetails && '*'}</Label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="text"
                value={formData.account_number}
                onChange={(e) => setFormData({...formData, account_number: e.target.value.replace(/\D/g, '')})}
                placeholder={hasDetails ? "Enter new account number to update" : "Enter account number"}
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                maxLength={20}
              />
            </div>
            {hasDetails && (
              <p className="text-xs text-slate-500 mt-1">
                Leave blank to keep existing account number
              </p>
            )}
          </div>

          {/* Account Type */}
          <div>
            <Label className="text-slate-300">Account Type *</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="account_type"
                  value="savings"
                  checked={formData.account_type === 'savings'}
                  onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                  className="w-4 h-4 text-emerald-500 bg-slate-800 border-slate-600 focus:ring-emerald-500"
                />
                <span className="text-white">Savings</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="account_type"
                  value="chequing"
                  checked={formData.account_type === 'chequing'}
                  onChange={(e) => setFormData({...formData, account_type: e.target.value})}
                  className="w-4 h-4 text-emerald-500 bg-slate-800 border-slate-600 focus:ring-emerald-500"
                />
                <span className="text-white">Chequing</span>
              </label>
            </div>
          </div>

          {/* Account Holder Name */}
          <div>
            <Label className="text-slate-300">Account Holder Name *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                type="text"
                value={formData.account_holder_name}
                onChange={(e) => setFormData({...formData, account_holder_name: e.target.value})}
                placeholder="Name as it appears on account"
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Security Notice */}
          <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-400">
              Your bank details are securely stored and will only be used to transfer your earnings. 
              We never share your information with third parties.
            </p>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {hasDetails ? 'Update Bank Details' : 'Save Bank Details'}
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
